import React, { forwardRef, memo, useContext, SVGAttributes } from 'react';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { Scale } from './Types';
import { noTransition, useTicks } from './utils';
import VizContext from './VizContext';

export interface Props extends Omit<SVGAttributes<SVGGElement>, 'scale'> {
  /**
   * Flag for enabling animations.
   */
  animate?: boolean;
  /**
   * The d3Scale that should be used for scaling the ticks
   * positions.
   */
  scale: Scale;
  /**
   * Number of desired grid lines to be rendered. The actual
   * number of rendered lines may differ, see d3-scale for
   * more information.
   */
  tickCount?: number;
  /**
   * Explicit values to render grid lines for.
   */
  tickValues?: number[];
  /**
   * The lower x value to render the grid lines from.
   */
  x0: number;
  /**
   * The upper x value to render the grid lines to.
   */
  x1: number;
  /**
   * The lower y value to render the grid lines from.
   */
  y0: number;
  /**
   * The upper y value to render the grid lines to.
   */
  y1: number;
}

/**
 * The multi axis grid lines that will be rendered according to the domains
 * given on the Viz component.
 */
export const VizSVGGridLines = forwardRef<SVGGElement, Props>((props: Props, ref) => {
  const {
    animate,
    className,
    opacity = 1,
    stroke = 'currentColor',
    scale,
    tickCount,
    tickValues,
    x0,
    x1,
    y0,
    y1,
    ...rest
  } = props;

  const {
    artboardBottom,
    artboardHeight,
    artboardLeft,
    artboardRight,
    artboardTop,
    artboardWidth,
    transition,
  } = useContext(VizContext);

  const { r, ticks } = useTicks({
    count: tickCount,
    scale: scale,
    values: tickValues,
    x0: x0,
    x1: x1,
    y0: y0,
    y1: y1,
  });

  const cos = Math.cos(r + (Math.PI / 2));
  const sin = Math.sin(r + (Math.PI / 2));

  const ticksEndpoints = ticks.map(([tick, px, py]) => [
    tick,
    Math.max(px - (artboardWidth * cos), artboardLeft),
    Math.min(px + (artboardWidth * cos), artboardRight),
    Math.max(py - (artboardHeight * sin), artboardTop),
    Math.min(py + (artboardHeight * sin), artboardBottom),
  ]);

  return (
    <g { ...rest }
        className={ classnames('Bitviz__grid-lines', className) }
        ref={ ref }>
      { ticksEndpoints.map(([tick, x1, x2, y1, y2]) => (
        <motion.line
            animate={ { opacity, x1, x2, y1, y2 } }
            initial={ { opacity: 0, x1: x1, x2: x2, y1: y1, y2: y2 } }
            key={ tick }
            stroke={ stroke }
            transition={ animate ? transition : noTransition } />
      )) }
    </g>
  );
});

export default memo(VizSVGGridLines);
